import { create } from 'zustand'
import {
  applyCreditOptions,
  jobTypeOptions,
  socialSecurityOptions,
  accumulationFundOptions,
  insurancePolicyOptions,
  carProductionOptions,
  realEstateOptions,
  creditSesameOptions,
  creditOptions,
  payoffFormOptions
} from '@/constants/formOptions'
import cityDate from '@/assets/js/city'
import activityApi from '@/apis/activity'
import { useAdChannelStore } from './adChannelCode.store'

// 随机获取options中的其中一个option
const randomOption = (options: {value: string}[], ignoreOptions: number[]|string[] = []) => {
  const filteredOptions = options.filter(option => !ignoreOptions.includes(option.value as never))
  const randomIndex = Math.floor(Math.random() * filteredOptions.length)
  return filteredOptions[randomIndex].value
}

export interface AutoForm {
  adChannelCode: string, // 渠道码
  job: string, // 职业
  isHuaWeiPhone: boolean, // 是否华为手机
  applyLimit: string, // 借款金额
  socialSecurity: string, // 社保
  housingFund: string, // 公积金
  insurance: string, // 保险保单
  houseProperty: string, // 房产
  carProperty: string, // 车辆
  sesameCredit: string, // 芝麻分
  realName: string, // 真实姓名
  identity: string, // 身份证号
  workCity: string, // 所在城市
  workCityCode: string, // 所在城市编码
  creditInvestigation: string, // 信用情况
  payoffForm: string, // 工资发放方式
}

interface AutoFormState {
  form: AutoForm,
  isUseful: boolean | null,
  status: 'initial' | 'loading' | 'success' | 'failed'
  updateForm: (form: Partial<AutoForm>) => void
  resetForm: () => void
  makePartOfAutoForm: (adChannelCode: string) => Promise<void>
  checkAutoFormUseful: (adChannelCode: string) => Promise<void>
}

const initialForm: AutoForm = {
  adChannelCode: '',
  job: '',
  isHuaWeiPhone: false,
  applyLimit: '',
  socialSecurity: '',
  housingFund: '',
  insurance: '',
  houseProperty: '',
  carProperty: '',
  sesameCredit: '',
  realName: '',
  identity: '',
  workCity: '',
  workCityCode: '',
  creditInvestigation: '',
  payoffForm: '',
}

export const useAutoFormStore = create<AutoFormState>(set => ({
  form: initialForm,
  isUseful: null,
  status: 'initial',

  updateForm: newForm => set(state => ({
    form: {
      ...state.form,
      ...newForm
    }
  })),

  resetForm: () => set(() => ({
    form: initialForm,
    status: 'initial'
  })),

  makePartOfAutoForm: async adChannelCode => {
    set({ status: 'loading' })
    try {
      const autoForm = {
        adChannelCode,
        isHuaWeiPhone: false,
        applyLimit: randomOption(applyCreditOptions, ['4']),
        job: randomOption(jobTypeOptions),
        socialSecurity: randomOption(socialSecurityOptions),
        housingFund: randomOption(accumulationFundOptions),
        insurance: randomOption(insurancePolicyOptions),
        houseProperty: randomOption(realEstateOptions),
        carProperty: randomOption(carProductionOptions),
        sesameCredit: randomOption(creditSesameOptions),
        identity: '',
        workCity: '',
        workCityCode: '',
        creditInvestigation: randomOption(creditOptions),
        payoffForm: randomOption(payoffFormOptions),
      }

      const res = await activityApi.reverseGeo({ adChannelCode })
      const { province, city } = res.data
      let workCityCode = ''
      cityDate.forEach(item => {
        if (item.label === province) {
          workCityCode = item.value
          item.children.forEach(cityItem => {
            if (cityItem.label === city) {
              workCityCode = `${workCityCode},${cityItem.value}`
            }
          })
        }
      })
      autoForm.workCityCode = workCityCode
      autoForm.workCity = `${province},${city}`
      autoForm.isHuaWeiPhone = navigator.userAgent.toLowerCase().includes('huawei')

      set(state => ({
        form: {
          ...state.form,
          ...autoForm
        },
        status: 'success'
      }))
    } catch (error) {
      set({ status: 'failed' })
      throw error
    }
  },

  checkAutoFormUseful: async adChannelCode => {
    const haierCode = ['r97gwM', 'dhT0rS'] // 海尔消金

    if (!haierCode.includes(adChannelCode)) {
      set({ isUseful: false })
      return
    }

    try {
      const { getCodeDetails } = useAdChannelStore.getState()
      const asyncReverseGeo = activityApi.reverseGeo({ adChannelCode })

      const channelCodeDetail = await getCodeDetails(adChannelCode)

      // 注册-下载 不启用
      if (Number(channelCodeDetail.linkType) === 1) {
        set({ isUseful: false })
        return
      }

      const geoRes = await asyncReverseGeo
      const { province, city } = geoRes.data

      const cityCodes = cityDate.find(item => item.label === province)
        ?.children.find(item => item.label === city)?.value

      const isUseful = !(channelCodeDetail.excludeCityCodes
        && cityCodes
        && channelCodeDetail.excludeCityCodes.includes(cityCodes)
      )

      set({ isUseful })
    } catch (error) {
      console.error(error)
      set({ isUseful: false })
    }
  }
}))